/**
 * The decision engine (AI-03, AI-04, AI-07, AI-08).
 *
 *   chooseAction(view, memory, profile, budget, seed)  → the move for this turn
 *   chooseKnock(view, memory, profile, budget, seed)   → knock after an exchange?
 *
 * Every legal move is a candidate (every hand-card × middle-card swap, each with and without a knock
 * straight after, taking the middle with and without a knock, each blind discard, knock, pass,
 * reveal). Each candidate is played out in the same sampled worlds (common random numbers, so the
 * comparison is fair and low-noise), with every seat then following the quick rollout policy.
 * Each finished hand is scored with the engine's evaluator and scoring, turned into everyone's
 * match totals, and valued as U = estimated chance of winning the match (matchUtil.ts).
 * Weak candidates are dropped after a few worlds and the survivors get more (successive halving).
 *
 * Timing: a blind draw's discard is chosen before the draw, exactly as in the engine, so the drawn
 * card in a world is only seen by the simulated future; knocking after a draw is a separate
 * decision (chooseKnock) made once the real card is in hand.
 */
import { type Action, type TurnView, legalize, mustReveal } from "../whiskey.ts";
import type { Card } from "../cards.ts";
import { evaluate, compareHands } from "../handEval.ts";
import { makeRng } from "../rng.ts";
import { borders } from "../states.ts";
import { cloneSim, applyAction, endTurn, playOut, showdown, type SimState, type RolloutPolicy } from "./sim.ts";
import { makeRolloutPolicy } from "./rollout.ts";
import { sampleWorld, worldViewOf } from "./sample.ts";
import { matchWinProb } from "./matchUtil.ts";
import { debugOn, type Budget, type Profile } from "./config.ts";
import type { CardMemory } from "./memory.ts";
import { AIController } from "../ai.ts";

export interface Candidate { action: Action; knockAfter: boolean; label: string }
/** What a profile may weigh among near-equal options; `hand` = the resulting hand when it is known now. */
interface Extra { knock: boolean; take: boolean; hand?: ReturnType<typeof evaluate>; feeds?: string }
export interface CandidateStats { label: string; n: number; meanU: number; sdU: number; meanPoints: number }
export interface Diagnostics {
  kind: "action" | "knock";
  profile: string;
  elapsedMs: number;
  worldsUsed: number;
  candidates: CandidateStats[];
  chosen: string;
  /** "timeout" / "stale" if the search stopped early, "baseline" if nothing was evaluated. */
  fallback?: string;
  note: "values are estimates from sampled worlds, not facts";
}
export interface Decision<T> { choice: T; diag: Diagnostics }

const ACTION_LABEL = (a: Action, knockAfter: boolean): string =>
  (a.type === "SWAP_ONE" ? `SWAP h${a.give}<-m${a.take}` : a.type === "BLIND" ? `BLIND give h${a.give}` : a.type) + (knockAfter ? "+KNOCK" : "");

/** Every legal move for this view. */
export function candidatesFor(v: TurnView): Candidate[] {
  const legal = new Set(v.legal);
  const canKnockAfter = legal.has("KNOCK");
  const out: Candidate[] = [];
  const add = (action: Action, knockAfter = false) => out.push({ action, knockAfter, label: ACTION_LABEL(action, knockAfter) });
  const both = (action: Action) => { add(action); if (canKnockAfter) add(action, true); };
  if (legal.has("KEEP")) add({ type: "KEEP" });
  if (legal.has("KNOCK")) add({ type: "KNOCK" });
  if (legal.has("REVEAL")) add({ type: "REVEAL" });
  if (legal.has("TAKE_MIDDLE")) both({ type: "TAKE_MIDDLE" });
  if (legal.has("SWAP_ONE")) for (let t = 0; t < 5; t++) for (let g = 0; g < 5; g++) both({ type: "SWAP_ONE", give: g, take: t });
  if (legal.has("BLIND")) for (let g = 0; g < 5; g++) add({ type: "BLIND", give: g });
  return out;
}

/** Four cards of one suit, or four ranks inside a five-rank window (a straight draw). */
function drawPotential(h: Card[]): number {
  const suits = new Map<string, number>();
  for (const c of h) suits.set(c.suit, (suits.get(c.suit) ?? 0) + 1);
  const flush = Math.max(...suits.values()) >= 4 ? 1 : 0;
  const ranks = [...new Set(h.flatMap((c) => (c.rank === 14 ? [1, 14] : [c.rank])))].sort((a, b) => a - b);
  let straight = 0;
  for (const lo of ranks) if (ranks.filter((r) => r >= lo && r < lo + 5).length >= 4) straight = 1;
  return flush + straight;
}

/**
 * Static screen before simulation: of the 25 single swaps, keep those giving the best few hands by
 * the evaluator (all tie-breaks), plus the best few that build a flush or straight draw. Every
 * other move type always goes to simulation.
 */
export function screenSwaps(cands: Candidate[], v: TurnView, keepBest = 6, keepDraws = 3): Candidate[] {
  const swaps = cands.filter((c) => c.action.type === "SWAP_ONE");
  if (!swaps.length) return cands;
  const scored = new Map<string, { rank: ReturnType<typeof evaluate>; draw: number }>();
  for (const c of swaps) {
    const a = c.action as { give: number; take: number };
    const h = v.hand.slice(); h[a.give] = v.middle[a.take]!;
    scored.set(`${a.give}-${a.take}`, { rank: evaluate(h), draw: drawPotential(h) });
  }
  const keys = [...scored.keys()];
  const byRank = keys.slice().sort((x, y) => compareHands(scored.get(y)!.rank, scored.get(x)!.rank)).slice(0, keepBest);
  const byDraw = keys.filter((k) => scored.get(k)!.draw > 0 && !byRank.includes(k))
    .sort((x, y) => scored.get(y)!.draw - scored.get(x)!.draw || compareHands(scored.get(y)!.rank, scored.get(x)!.rank)).slice(0, keepDraws);
  const keep = new Set([...byRank, ...byDraw]);
  return cands.filter((c) => c.action.type !== "SWAP_ONE" || keep.has(`${(c.action as { give: number }).give}-${(c.action as { take: number }).take}`));
}

const policyCache = new Map<number, RolloutPolicy>();
const rolloutFor = (eagerness: number): RolloutPolicy => {
  const key = Math.round(eagerness * 20) / 20;
  let p = policyCache.get(key);
  if (!p) { p = makeRolloutPolicy(key); policyCache.set(key, p); }
  return p;
};

const mix = (a: number, b: number): number => (Math.imul(a ^ 0x9e3779b9, 0x85ebca6b) ^ Math.imul(b + 0x632be5ab, 0xc2b2ae35)) >>> 0;

interface Ctx {
  v: TurnView; mem: CardMemory; profile: Profile; budget: Budget; seed: number;
  worlds: SimState[]; policies: RolloutPolicy[]; isCurrent?: () => boolean;
}

/** Score a finished (or horizon-capped) simulated hand: U for `me`, plus my hand points. */
function valueOf(s: SimState, ctx: Ctx): { u: number; pts: number } {
  const t = ctx.v.table, me = ctx.mem.me;
  const sd = showdown(s.hands);
  const winner = sd.order[0];
  const owned = t.players[winner].states;
  let contig = 0;
  if (t.stateMethod === "RANDOM") contig = ctx.v.targetState && owned.some((o) => borders(ctx.v.targetState!, o)) ? 1 : 0;
  else contig = t.unclaimed.some((u) => owned.some((o) => borders(u, o))) ? 1 : 0;
  const pts = t.players.map((p, i) => p.score + sd.points[i] + (i === winner ? contig : 0));
  const states = t.players.map((p, i) => p.states.length + (i === winner ? 1 : 0));
  return { u: matchWinProb(me, pts, states, t.handsAfterThis), pts: sd.points[me] + (me === winner ? contig : 0) };
}

function runCandidate(c: Candidate | { post: boolean }, w: number, ctx: Ctx): { u: number; pts: number } {
  const s = cloneSim(ctx.worlds[w]);
  const rng = makeRng(mix(ctx.seed, w));
  const me = ctx.mem.me;
  if ("post" in c) {
    // our exchange is already made; decide only whether to knock now
    s.turnsTaken[me]--;
    endTurn(s, { type: "SWAP_ONE", give: 0, take: 0 }, c.post && s.knockerSeat === -1);
  } else {
    let a = c.action, knockAfter = c.knockAfter;
    if (mustReveal(a, s.middleRevealed, me === s.dealerSeat)) {
      s.middleRevealed = true;
      s.justRevealed = true;
      ({ action: a, knockAfter } = ctx.policies[me](s, me, rng));
    }
    a = legalize(a, { revealed: s.middleRevealed, knocked: s.knockerSeat !== -1, dealer: me === s.dealerSeat, justRevealed: s.justRevealed });
    applyAction(s, a, rng);
    const exchanged = a.type === "SWAP_ONE" || a.type === "TAKE_MIDDLE" || a.type === "BLIND";
    endTurn(s, a, knockAfter && exchanged && s.knockerSeat === -1);
  }
  playOut(s, ctx.policies, rng, ctx.budget.horizon);
  return valueOf(s, ctx);
}

/**
 * `u` holds, per world, U plus a tie-breaker of 1e-6 × our hand points: when the match outcome does
 * not depend on the move (U flat, e.g. a decided last hand) the AI still takes the points. The
 * tie-breaker is far below any U difference the sampling can resolve, so it never outweighs U.
 */
interface Row<T> { item: T; label: string; u: number[]; pts: number }

async function search<T>(items: { item: T; label: string; run: (w: number) => { u: number; pts: number } }[], ctx: Ctx, kind: "action" | "knock",
  extra: (r: Row<T>) => Extra): Promise<{ row: Row<T> | null; diag: Diagnostics }> {
  const t0 = now();
  let lastYield = t0, fallback: string | undefined;
  let rows: Row<T>[] = items.map((x) => ({ item: x.item, label: x.label, u: [], pts: 0 }));
  let done = 0; // worlds every surviving row has completed
  outer: for (const round of ctx.budget.rounds) {
    const end = Math.min(ctx.worlds.length, done + Math.max(1, Math.round(round.share * ctx.budget.worlds)));
    for (const r of rows) {
      const run = items.find((x) => x.item === r.item)!.run;
      for (let w = done; w < end; w++) { const o = run(w); r.u.push(o.u + 1e-6 * o.pts); r.pts += o.pts; }
      const t = now();
      if (t - t0 > ctx.budget.maxMs) { fallback = "timeout"; break outer; }
      if (ctx.budget.yieldEveryMs && t - lastYield > ctx.budget.yieldEveryMs) {
        await new Promise((res) => setTimeout(res, 0)); lastYield = now();
        if (ctx.isCurrent && !ctx.isCurrent()) { fallback = "stale"; break outer; }
      }
    }
    done = end;
    rows.sort((a, b) => mean(b.u) - mean(a.u));
    rows = rows.slice(0, Math.max(1, round.keep));
  }
  // compare only on worlds every remaining row completed
  const n = Math.min(...rows.map((r) => r.u.length));
  rows = rows.filter((r) => r.u.length >= n && n > 0);
  for (const r of rows) r.u = r.u.slice(0, n);
  rows.sort((a, b) => mean(b.u) - mean(a.u));
  const chosen = rows.length ? pickByProfile(rows, ctx, extra) : null;
  const diag: Diagnostics = {
    kind, profile: ctx.profile.name, elapsedMs: now() - t0, worldsUsed: n,
    candidates: rows.map((r) => ({ label: r.label, n: r.u.length, meanU: mean(r.u), sdU: sd(r.u), meanPoints: r.pts / Math.max(1, r.u.length) })),
    chosen: chosen?.label ?? "baseline", fallback: chosen ? fallback : "baseline",
    note: "values are estimates from sampled worlds, not facts",
  };
  if (debugOn()) console.debug("[poli-ai]", JSON.stringify(diag));
  return { row: chosen, diag };
}

/**
 * Personality acts only among statistically near-best options (AI-05): a candidate is "near" when
 * its mean U is within `nearZ` paired standard errors of the best. Anything clearly worse is never
 * chosen, whatever the profile.
 */
function pickByProfile<T>(rows: Row<T>[], ctx: Ctx, extra: (r: Row<T>) => Extra): Row<T> {
  const best = rows[0], p = ctx.profile;
  let near = rows.filter((r) => {
    if (r === best) return true;
    const d = r.u.map((x, i) => best.u[i] - x);
    const se = sd(d) / Math.sqrt(d.length);
    return mean(d) <= p.nearZ * se + 1e-6;
  });
  // Drop a near option whose known resulting hand is strictly weaker than another near option's
  // with the same follow-up and the same card handed to the middle (e.g. sevens full when kings full
  // is on offer for the same discard): sampling noise must not pick a dominated hand.
  const ex = new Map(near.map((r) => [r, extra(r)]));
  near = near.filter((r) => {
    const a = ex.get(r)!;
    return !a.hand || !near.some((o) => { const b = ex.get(o)!; return o !== r && b.hand && b.knock === a.knock && b.feeds === a.feeds && compareHands(b.hand, a.hand!) > 0; });
  });
  if (!near.includes(best)) near.sort((a, b) => mean(b.u) - mean(a.u));
  if (near.length === 1) return near[0];
  const unit = Math.max(1e-5, Math.max(...near.map((r) => mean(best.u) - mean(r.u))), sd(best.u) / Math.sqrt(best.u.length));
  const sdBest = Math.max(1e-9, sd(best.u));
  const rng = makeRng(mix(ctx.seed, 0x7a11));
  let pick = near[0], top = -Infinity;
  for (const r of near) {
    const e = extra(r);
    const score = mean(r.u)
      + unit * (p.riskPreference * (sd(r.u) - sdBest) / sdBest)
      + unit * ((e.knock ? p.knockBias : 0) + (e.take ? p.takeMiddleBias : 0) + p.exploration * rng.next());
    if (score > top) { top = score; pick = r; }
  }
  return pick;
}

function makeCtx(v: TurnView, mem: CardMemory, profile: Profile, budget: Budget, seed: number, isCurrent?: () => boolean): Ctx {
  const rng = makeRng(seed);
  const wv = worldViewOf(v);
  const worlds: SimState[] = [];
  for (let i = 0; i < budget.worlds; i++) worlds.push(sampleWorld(wv, mem, rng, profile.useInference));
  const policies = [0, 1, 2, 3, 4].map((s) => (s !== mem.me && profile.useInference ? rolloutFor(mem.eagerness(s)) : rolloutFor(0.5)));
  return { v, mem, profile, budget, seed, worlds, policies, isCurrent };
}

/** The hand a move leaves us with, when it is known without drawing (swap, face-up take, pass, knock). */
function knownResult(v: TurnView, a: Action): ReturnType<typeof evaluate> | undefined {
  if (a.type === "SWAP_ONE") { const h = v.hand.slice(); h[a.give] = v.middle[a.take]!; return evaluate(h); }
  if (a.type === "TAKE_MIDDLE" && v.middleRevealed) return evaluate(v.middle as Card[]);
  if (a.type === "KEEP" || a.type === "KNOCK") return evaluate(v.hand);
  return undefined;
}

/** What the move puts face up in the middle for the others. */
function fedCard(v: TurnView, a: Action): string {
  if (a.type === "SWAP_ONE") return `${v.hand[a.give].rank}${v.hand[a.give].suit}`;
  if (a.type === "TAKE_MIDDLE") return "hand";
  return a.type === "BLIND" ? "blind" : "";
}

const baseline = new AIController(1);

/**
 * House rules on top of the search (Scott, 3 Nov 2026):
 *  - Never take a face-up card (or the whole middle) that does nothing for our own hand — no taking
 *    a card just because someone else wants it. A take must make the hand better, or (before a knock,
 *    while there are turns left) give a flush or straight draw it didn't have.
 *  - After a knock it's the last turn: if a face-up move makes our hand better, make the best one
 *    (the whole middle included), whatever it hands the players still to come.
 */
export function ownHandFirst(cands: Candidate[], v: TurnView): { cands: Candidate[]; forced?: Candidate } {
  const current = evaluate(v.hand), draw = drawPotential(v.hand);
  const takes = (c: Candidate) => c.action.type === "SWAP_ONE" || (c.action.type === "TAKE_MIDDLE" && v.middleRevealed);
  const kept = cands.filter((c) => {
    if (!takes(c)) return true;
    const h = c.action.type === "SWAP_ONE" ? (() => { const x = v.hand.slice(); x[(c.action as { give: number }).give] = v.middle[(c.action as { take: number }).take]!; return x; })() : (v.middle as Card[]);
    return compareHands(evaluate(h), current) > 0 || (!v.someoneKnocked && drawPotential(h) > draw);
  });
  if (v.someoneKnocked) {
    let forced: Candidate | undefined, best = current;
    for (const c of kept) {
      if (!takes(c) || c.knockAfter) continue;
      const r = knownResult(v, c.action)!;
      if (compareHands(r, best) > 0) { best = r; forced = c; }
    }
    if (forced) return { cands: kept, forced };
  }
  return { cands: kept.length ? kept : cands };
}

export async function chooseAction(v: TurnView, mem: CardMemory, profile: Profile, budget: Budget, seed: number, isCurrent?: () => boolean): Promise<Decision<Candidate>> {
  const ctx = makeCtx(v, mem, profile, budget, seed, isCurrent);
  const own = ownHandFirst(candidatesFor(v), v);
  if (own.forced) return { choice: own.forced, diag: { ...emptyDiag("action", profile), chosen: own.forced.label + " (best hand after the knock)" } };
  const cands = screenSwaps(own.cands, v);
  const { row, diag } = await search(cands.map((c) => ({ item: c, label: c.label, run: (w: number) => runCandidate(c, w, ctx) })), ctx, "action",
    (r) => ({ knock: r.item.action.type === "KNOCK" || r.item.knockAfter, take: r.item.action.type === "TAKE_MIDDLE", hand: knownResult(v, r.item.action), feeds: fedCard(v, r.item.action) }));
  if (row) return { choice: row.item, diag };
  const a = baseline.decideAction(v); // documented legal fallback
  return { choice: { action: a, knockAfter: false, label: "baseline " + a.type }, diag };
}

export async function chooseKnock(v: TurnView, mem: CardMemory, profile: Profile, budget: Budget, seed: number, isCurrent?: () => boolean): Promise<Decision<boolean>> {
  if (!v.legal.includes("KNOCK")) return { choice: false, diag: emptyDiag("knock", profile) };
  const ctx = makeCtx(v, mem, profile, budget, seed, isCurrent);
  const opts = [false, true].map((k) => ({ item: k, label: k ? "KNOCK now" : "carry on", run: (w: number) => runCandidate({ post: k }, w, ctx) }));
  const { row, diag } = await search(opts, ctx, "knock", (r) => ({ knock: r.item, take: false }));
  return { choice: row ? row.item : false, diag };
}

function emptyDiag(kind: "action" | "knock", p: Profile): Diagnostics {
  return { kind, profile: p.name, elapsedMs: 0, worldsUsed: 0, candidates: [], chosen: "-", note: "values are estimates from sampled worlds, not facts" };
}

const now = (): number => (typeof performance !== "undefined" ? performance.now() : Date.now());
export function mean(a: number[]): number { let s = 0; for (const x of a) s += x; return a.length ? s / a.length : 0; }
export function sd(a: number[]): number {
  if (a.length < 2) return 0;
  const m = mean(a); let s = 0; for (const x of a) s += (x - m) ** 2; return Math.sqrt(s / (a.length - 1));
}
